import React, { useEffect, useState } from 'react';
import { X, Smartphone } from 'lucide-react';

/**
 * Mobile-only floating App banner.
 * Shows a small "App holen" pill at bottom-right after the user has scrolled
 * a bit. Dismissible — won't reappear in the same session (sessionStorage).
 * Hidden on desktop (lg breakpoint and up).
 */
export function MobileAppBanner({ playStoreUrl, appStoreUrl }) {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Don't show again in the same session if user dismissed it
    try {
      if (sessionStorage.getItem('app_banner_dismissed') === '1') {
        setDismissed(true);
        return;
      }
    } catch { /* ignore */ }

    const onScroll = () => {
      if (window.scrollY > 250) setVisible(true);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    // Also show after 5s even without scroll
    const t = setTimeout(() => setVisible(true), 5000);
    return () => {
      window.removeEventListener('scroll', onScroll);
      clearTimeout(t);
    };
  }, []);

  const dismiss = () => {
    try { sessionStorage.setItem('app_banner_dismissed', '1'); } catch { /* ignore */ }
    setDismissed(true);
  };

  // Detect mobile OS to send the user to the right store
  const targetUrl = (() => {
    if (typeof navigator === 'undefined') return playStoreUrl;
    const ua = navigator.userAgent || '';
    if (/iPad|iPhone|iPod/.test(ua)) return appStoreUrl || playStoreUrl;
    return playStoreUrl;
  })();

  if (dismissed || !visible) return null;

  return (
    <div
      className="lg:hidden fixed bottom-4 right-4 left-4 z-50 animate-in fade-in slide-in-from-bottom-4"
      data-testid="mobile-app-banner"
      style={{ animationDuration: '300ms' }}
    >
      <div className="bg-slate-900 text-white rounded-2xl shadow-2xl border border-slate-800 px-4 py-3 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-orange-500 flex items-center justify-center shrink-0">
          <Smartphone size={20} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold leading-tight">366Transfer App</p>
          <p className="text-xs text-slate-400 mt-0.5 leading-tight">Schneller buchen — Push-Benachrichtigungen</p>
        </div>
        <a
          href={targetUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold rounded-lg px-3 py-2 whitespace-nowrap"
          data-testid="mobile-app-banner-cta"
        >
          App holen
        </a>
        <button
          onClick={dismiss}
          className="text-slate-400 hover:text-white p-1"
          aria-label="Banner schliessen"
          data-testid="mobile-app-banner-close"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
